import { useState } from 'react'
import { compact } from 'lodash'
import { useTranslation } from 'react-i18next'
import { FieldErrors } from 'react-hook-form'
import { t, Trans } from '@lingui/macro'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import { Box, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material'

import colors from '@/assets/scss/_themes-vars.module.scss'
import TabPanel from '@/components/ui/tab/TabPanel'
import Tabs from '@/components/ui/tabs/Tabs'
import { Patient } from '@/core/models/patient.model'
import { PatientProfileFormData } from '@/features/patient/patient-profile-form'
import { isProvisionalIdentityValue } from '@/features/patient/patient-profile-form/lib/identity-naming'
import { PatientConsultations } from '@/features/patient/patient-consultations'
import { PatientDocuments } from '@/features/patient/patient-documents'
import { useIsUS } from '@/hooks/useIsUS'
import { useIsSA } from '@/hooks/useIsSA'

import { usePatientDetailsMinHeight } from '../model/usePatientDetailsMinHeight'
import { AccountStatus } from './AccountStatus'
import { IdentityStatus } from './IdentityStatus'
import { PatientIdentityAuditDialog } from './PatientIdentityAuditDialog'
import { PatientInfo } from './PatientInfo'
import { useIsINSEnabled } from '@/shared/model/useIsINSEnabled'
import { PatientForms } from '@/features/patient/patient-forms'

const formatDisplayNamePart = (value?: string | null) => (isProvisionalIdentityValue(value) ? t`Unknown` : value || '')

type PatientDetailsProps = {
  errors: FieldErrors<PatientProfileFormData>
  patient: Patient
  showInformationTab: boolean
  showPastConsultationsTab: boolean
  showDocumentsTab: boolean
  showFormsTab: boolean
}

export function PatientDetails({ errors, patient, showInformationTab, showPastConsultationsTab, showDocumentsTab, showFormsTab }: PatientDetailsProps) {
  const i18next = useTranslation()
  const { isINSEnabled } = useIsINSEnabled()
  const isUS = useIsUS()
  const isSA = useIsSA()
  const [isIdentityAuditOpen, setIsIdentityAuditOpen] = useState(false)

  const { minHeight, ref: paperRef } = usePatientDetailsMinHeight()

  const tabs = compact([
    showInformationTab && { label: t`Information`, component: <PatientInfo errors={errors} patientId={patient._id} /> },
    showPastConsultationsTab && {
      label: t`Past consultations`,
      component: <PatientConsultations patientId={patient._id} />,
    },
    showDocumentsTab && { label: t`Documents`, component: <PatientDocuments patientId={patient._id} /> },
    showFormsTab && { label: i18next.t('page.infoPatient.forms.label'), component: <PatientForms patientId={patient._id} /> },
  ])

  return (
    <Paper ref={paperRef} sx={{ minHeight: minHeight ? minHeight + 'px' : 'unset' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: '20px' }}>
        <Stack>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="subtitle1" sx={{ fontSize: '18px', fontWeight: 'bold' }}>
              {`${formatDisplayNamePart(patient?.userInfo.surname || patient?.userInfo.birthSurname)} ${formatDisplayNamePart(
                patient?.userInfo.name || patient?.userInfo.birthFirstName
              )}`.trim()}
            </Typography>
            {isINSEnabled && (
              <Tooltip title={t`View identity audit history`}>
                <IconButton aria-label={t`View identity audit history`} color="primary" onClick={() => setIsIdentityAuditOpen(true)} size="small">
                  <HistoryRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {isINSEnabled && <IdentityStatus />}
            <AccountStatus patient={patient} />
          </Stack>
          <Typography color="primary" variant="subtitle2" sx={{ fontSize: '14px', fontWeight: 'bold' }}>
            <Trans>Patient Information</Trans>
          </Typography>
        </Stack>
        <Stack>
          <Typography variant="subtitle1" sx={{ color: colors.gray500, fontSize: '14px' }}>
            {isUS ? i18next.t('field.secu.label') : isSA ? <Trans>National ID / Iqama ID / Border Number</Trans> : <Trans>Social number</Trans>}
          </Typography>
          <Typography variant="body1" sx={{ color: colors.gray900, fontSize: '14px' }}>
            {isUS ? (
              patient?.primaryPolicy
            ) : isSA && (!patient?.secu || patient.secu === '000000000000000') ? (
              // '000000000000000' is the mongoose placeholder stored when no identification was captured.
              <Trans>Unknown</Trans>
            ) : (
              patient?.secu
            )}
          </Typography>
        </Stack>
      </Stack>
      <Divider />
      <Tabs
        renderTabPanel={({ value }) => (
          <Box sx={{ p: '20px 16px' }}>
            {tabs.map((tab, index) => (
              <TabPanel key={index} value={value} index={index}>
                {tab.component}
              </TabPanel>
            ))}
          </Box>
        )}
        tabs={tabs.map((tab) => ({ label: tab.label }))}
        variant="fullWidth"
      />
      {isINSEnabled && (
        <PatientIdentityAuditDialog
          identityStatusHistory={patient.identityStatusHistory}
          onClose={() => setIsIdentityAuditOpen(false)}
          open={isIdentityAuditOpen}
          patientId={patient._id}
        />
      )}
    </Paper>
  )
}
